/**
 * Color interpolation utility for smooth transitions between RGB colors
 */

// Color stops for waveform visualization
const WAVEFORM_COLORS = () => {
  const rootElement = document.documentElement;
  return {
    // Normal state - plenty of time left
    normal: getComputedStyle(rootElement).getPropertyValue('--secondary'),
    // Warning state - time running low
    warning: getComputedStyle(rootElement).getPropertyValue('--warning'),
    // Danger state - almost out of time
    danger: getComputedStyle(rootElement).getPropertyValue('--destructive'),
  };
};

// Time thresholds for color transitions
const TIME_THRESHOLDS = {
  normalToWarning: 15, // Start transitioning to warning at 15 seconds
  warningToDanger: 5, // Start transitioning to danger at 5 seconds
};

/**
 * Calculate waveform color based on remaining recording time
 * @param remainingSeconds - Seconds remaining in recording
 * @param maxRecordingSeconds - Maximum recording duration (default 60)
 * @returns CSS RGB color string
 */
export function getWaveformColor(
  remainingSeconds: number | undefined,
  maxRecordingSeconds: number = 60
): string {
  const waveformColors = WAVEFORM_COLORS();

  // Default to normal color if no time info available
  if (remainingSeconds === undefined) {
    const normalColor = waveformColors.normal;
    return normalColor;
  }

  // Ensure remaining seconds is within valid range
  const clampedRemainingSeconds = Math.max(0, Math.min(maxRecordingSeconds, remainingSeconds));

  let calculatedColor: string;

  if (clampedRemainingSeconds > TIME_THRESHOLDS.normalToWarning) {
    // Plenty of time left (60-25 seconds): Use normal color
    calculatedColor = waveformColors.normal;
  } else if (clampedRemainingSeconds > TIME_THRESHOLDS.warningToDanger) {
    calculatedColor = waveformColors.warning;
  } else {
    calculatedColor = waveformColors.danger;
  }

  return calculatedColor;
}
